import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getMeetings, updateMeetingStatus, getDealStats } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Plus,
  DotsSixVertical,
  Phone,
  Video,
  Users,
  Calendar,
  CurrencyDollar,
  ArrowRight,
  Microphone,
  MagnifyingGlass,
  FunnelSimple
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';

const COLUMNS = [
  { id: 'open', title: 'Open', color: 'bg-[#007AFF]', lightColor: 'bg-[#007AFF]/10', borderColor: 'border-[#007AFF]' },
  { id: 'pending', title: 'Pending', color: 'bg-amber-500', lightColor: 'bg-amber-50', borderColor: 'border-amber-500' },
  { id: 'closed_won', title: 'Won', color: 'bg-green-600', lightColor: 'bg-green-50', borderColor: 'border-green-600' },
  { id: 'closed_lost', title: 'Lost', color: 'bg-red-500', lightColor: 'bg-red-50', borderColor: 'border-red-500' }
];

export default function KanbanPage() {
  const [meetings, setMeetings] = useState([]);
  const [dealStats, setDealStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [meetingsRes, statsRes] = await Promise.all([
        getMeetings(),
        getDealStats()
      ]);
      setMeetings(meetingsRes.data);
      setDealStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredMeetings = useCallback(() => {
    let filtered = meetings;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.client_name?.toLowerCase().includes(q) ||
        m.client_company?.toLowerCase().includes(q) ||
        m.client_email?.toLowerCase().includes(q) ||
        m.deal_stage?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(m => (m.deal_status || 'open') === statusFilter);
    }
    return filtered;
  }, [meetings, searchQuery, statusFilter]);

  const getMeetingsByStatus = useCallback((status) => {
    return getFilteredMeetings().filter(m => (m.deal_status || 'open') === status);
  }, [getFilteredMeetings]);

  const getColumnValue = useCallback((status) => {
    return getMeetingsByStatus(status).reduce((sum, m) => sum + (m.deal_value || 0), 0);
  }, [getMeetingsByStatus]);

  const handleDragStart = (e, meeting) => {
    setDraggedItem(meeting);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', meeting.id);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedItem) return;
    
    const currentStatus = draggedItem.deal_status || 'open';
    if (currentStatus === newStatus) {
      setDraggedItem(null);
      return;
    }

    // Optimistic update
    setMeetings(prev => prev.map(m => 
      m.id === draggedItem.id ? { ...m, deal_status: newStatus } : m
    ));

    try {
      await updateMeetingStatus(draggedItem.id, newStatus);
      // Refresh stats
      const statsRes = await getDealStats();
      setDealStats(statsRes.data);
    } catch (error) {
      console.error('Failed to update status:', error);
      // Revert on error
      setMeetings(prev => prev.map(m => 
        m.id === draggedItem.id ? { ...m, deal_status: currentStatus } : m
      ));
    }
    
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverColumn(null);
  };

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case 'call': return <Phone size={12} />;
      case 'video': return <Video size={12} />;
      case 'in-person': return <Users size={12} />;
      default: return <Calendar size={12} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  return (
    <div className="h-full" data-testid="kanban-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1D1D1F]">
            Deal Pipeline
          </h1>
          <p className="text-[#86868B] mt-1">
            {searchQuery || statusFilter !== 'all' 
              ? `Showing ${getFilteredMeetings().length} of ${meetings.length} deals` 
              : 'Drag and drop deals between columns to update status'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/meetings/voice">
            <Button className="btn-accent h-11 px-5 rounded-xl" data-testid="voice-meeting-btn">
              <Microphone size={20} className="mr-2" />
              Voice
            </Button>
          </Link>
          <Link to="/meetings/new">
            <Button className="btn-primary h-11 px-6 rounded-xl" data-testid="new-meeting-btn">
              <Plus size={20} className="mr-2" />
              New Deal
            </Button>
          </Link>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#86868B]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search deals by name, company, or stage..."
            className="input-base h-11 pl-11 rounded-xl text-base"
            data-testid="deal-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44 h-11 rounded-xl border-gray-200" data-testid="deal-status-filter">
            <FunnelSimple size={16} className="mr-2 text-[#86868B]" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="closed_won">Won</SelectItem>
            <SelectItem value="closed_lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Bar */}
      {dealStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">Total Pipeline</p>
            <p className="text-2xl font-bold text-[#1D1D1F] mt-1">${dealStats.total_value.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">Total Deals</p>
            <p className="text-2xl font-bold text-[#1D1D1F] mt-1">{dealStats.total_deals}</p>
          </div>
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-green-700">Won Value</p>
            <p className="text-2xl font-bold text-green-700 mt-1">${dealStats.won_value.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">Win Rate</p>
            <p className="text-2xl font-bold text-[#1D1D1F] mt-1">{dealStats.win_rate}%</p>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 min-h-[500px]">
        {COLUMNS.map((column) => {
          const columnMeetings = getMeetingsByStatus(column.id);
          const columnValue = getColumnValue(column.id);
          const isDragOver = dragOverColumn === column.id;

          return (
            <div
              key={column.id}
              className={`flex flex-col rounded-xl transition-all ${
                isDragOver ? `${column.lightColor} ring-2 ${column.borderColor}` : 'bg-gray-50'
              }`}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.id)}
              data-testid={`column-${column.id}`}
            >
              {/* Column Header */}
              <div className={`${column.color} text-white p-4`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">
                    {column.title}
                  </h3>
                  <Badge className="bg-white/20 text-white rounded-full px-2">
                    {columnMeetings.length}
                  </Badge>
                </div>
                <p className="text-white/80 text-sm mt-1">
                  ${columnValue.toLocaleString()}
                </p>
              </div>

              {/* Column Content */}
              <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-[600px]">
                {columnMeetings.length === 0 ? (
                  <div className="text-center py-8 text-[#86868B]">
                    <p className="text-sm">No deals</p>
                    <p className="text-xs mt-1">Drag deals here</p>
                  </div>
                ) : (
                  columnMeetings.map((meeting) => (
                    <Link
                      key={meeting.id}
                      to={`/meetings/${meeting.id}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, meeting)}
                      onDragEnd={handleDragEnd}
                      className={`block bg-white border border-gray-200 p-4 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${
                        draggedItem?.id === meeting.id ? 'opacity-50' : ''
                      }`}
                      data-testid={`deal-card-${meeting.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <DotsSixVertical size={16} className="text-gray-400 mt-1 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#1D1D1F] truncate">
                            {meeting.client_name}
                          </h4>
                          {meeting.client_company && (
                            <p className="text-sm text-[#86868B] truncate">
                              {meeting.client_company}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 mt-2 text-xs text-[#86868B]">
                            <span className="flex items-center gap-1">
                              {getMeetingTypeIcon(meeting.meeting_type)}
                              {format(parseISO(meeting.meeting_date), 'MMM d')}
                            </span>
                            {meeting.deal_value && (
                              <span className="flex items-center gap-1 text-green-600 font-medium">
                                <CurrencyDollar size={12} />
                                {meeting.deal_value.toLocaleString()}
                              </span>
                            )}
                          </div>
                          
                          {meeting.deal_stage && (
                            <Badge className="mt-2 bg-gray-100 text-gray-700 rounded-xl text-xs capitalize">
                              {meeting.deal_stage}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
